import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { EntitiesState } from '@redux/entities'

import type { PublicationPaginationRuleModel, PublicationPaginationRuleRequestOptions } from '@models/types'

const watchEntityKeys = ['paginationRules', 'publicationPaginationRules', 'publications']

type SortKey = {
  sortKey?: 'brands' | 'paginationRules',
}

type HandleSortParams = {
  publicationPaginationRule: PublicationPaginationRuleModel,
  sortKey?: SortKey,
  entities: EntitiesState,
}

const handleSort = (params: HandleSortParams) => {
  const { entities, publicationPaginationRule, sortKey } = params
  const { brands, paginationRules, publications } = entities || {}

  // @ts-expect-error TS2367
  if (sortKey === 'brands'){
    const brandId = paginationRules[publicationPaginationRule.pagination_rule_id]?.brand_id
    const brand = brands[brandId] || {}

    return brand?.title
  }

  return publications[publicationPaginationRule.publication_id]?.title
}

type PublicationPaginationRuleFilters = {
  pagination_rule_id?: number,
  publication_id?: number,
  string?: string,
}

export type Options = {
  entityKey?: string,
  filters?: PublicationPaginationRuleFilters,
  performHttpRequests?: boolean,
  requestOptions?: PublicationPaginationRuleRequestOptions,
  sortKey?: SortKey,
}

function usePublicationPaginationRules(options: Options = {}) {
  const {
    entityKey, filters, requestOptions, sortKey,
  } = options

  const {
    pagination_rule_id: filterPaginationRuleId,
    publication_id: filterPublicationId,
    string: filterString,
  } = filters || {}

  const {
    updatedEntities: {
      paginationRules: paginationRulesUpdatedAt,
      publicationPaginationRules: publicationPaginationRulesUpdatedAt,
      publications: publicationsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const entities = useSelector(reduxState => reduxState.entities)
  const { publicationPaginationRules, publications } = entities || {}

  const filteredPublicationPaginationRules = useMemo((): PublicationPaginationRuleModel[] => {
    const filtered = Object.values(publicationPaginationRules).filter((publicationPaginationRule) => {
      const { pagination_rule_id, publication_id } = publicationPaginationRule

      const publication = publications[publication_id] || {}
      const { title: publicationTitle } = publication

      const matchPaginationRule = matchFilterNumber(pagination_rule_id, filterPaginationRuleId)
      const matchPublication = matchFilterNumber(publication_id, filterPublicationId)
      const matchPublicationTitle = matchFilterString(publicationTitle, filterString)

      return matchPaginationRule && matchPublication && matchPublicationTitle
    })

    return sortArrayBy(
      filtered,
      'asc',
      publicationPaginationRule => handleSort({ entities, publicationPaginationRule, sortKey }),
    )
  }, [paginationRulesUpdatedAt, publicationPaginationRulesUpdatedAt, publicationsUpdatedAt, JSON.stringify(filters)])

  const filteredPublicationPaginationRulesCount = filteredPublicationPaginationRules.length
  const hasFilteredPublicationPaginationRules = !!filteredPublicationPaginationRulesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPublicationPaginationRulesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPublicationPaginationRules } = useReduxAction(
    'publicationPaginationRules',
    'loadPublicationPaginationRules',
    {
      entityKey,
      limit,
      ...requestOptions,
      ...filtersWithOffset,
    },
    [filters, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublicationPaginationRules,
    filteredPublicationPaginationRulesCount,
    hasFilteredPublicationPaginationRules,
    loading: loadingPublicationPaginationRules,
    publicationPaginationRulesUpdatedAt,
  }
}

export default usePublicationPaginationRules
